import type {
  AdminDemoLeadConversionResult,
  AdminDocumentPackageDetail,
  AdminLegalProfile,
} from '../legalTypes';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const responseData = (error: unknown): Record<string, unknown> | null => {
  if (!isRecord(error) || !isRecord(error.response) || !isRecord(error.response.data)) {
    return null;
  }
  return error.response.data;
};

export const getAdminResponseStatus = (error: unknown): number | null => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return null;
  }
  return typeof error.response.status === 'number' ? error.response.status : null;
};

export const mustClearSensitiveAdminState = (error: unknown): boolean => {
  const status = getAdminResponseStatus(error);
  return status === 401 || status === 403 || status === 404;
};

const isConversionResult = (value: unknown): value is AdminDemoLeadConversionResult =>
  isRecord(value)
  && typeof value.companyId === 'number'
  && typeof value.companyLegalProfileId === 'string'
  && typeof value.packageId === 'string'
  && typeof value.jobId === 'string'
  && (typeof value.licenseId === 'number' || value.licenseId === null)
  && typeof value.invitationQueued === 'boolean'
  && typeof value.isReplay === 'boolean';

export const getDemoConversionConflict = (
  error: unknown
): AdminDemoLeadConversionResult | null => {
  const existing = responseData(error)?.existing;
  return isConversionResult(existing) ? existing : null;
};

const isNullableString = (value: unknown): value is string | null =>
  typeof value === 'string' || value === null;

const isPackageDetail = (value: unknown): value is AdminDocumentPackageDetail =>
  isRecord(value)
  && typeof value.id === 'string'
  && (typeof value.companyId === 'number' || value.companyId === null)
  && typeof value.companyLegalProfileId === 'string'
  && typeof value.sequence === 'number'
  && typeof value.purpose === 'string'
  && typeof value.state === 'string'
  && typeof value.templateSetVersion === 'string'
  && typeof value.templateSetHash === 'string'
  && isNullableString(value.parentPackageId)
  && isNullableString(value.failureCode)
  && typeof value.createdAtUtc === 'string'
  && typeof value.updatedAtUtc === 'string'
  && typeof value.version === 'number'
  && Array.isArray(value.availableCommands)
  && Array.isArray(value.signers);

export const getDocumentConflictCurrent = (
  error: unknown
): AdminDocumentPackageDetail | null => {
  const current = responseData(error)?.current;
  return isPackageDetail(current) ? current : null;
};

const LegalProfileStringFields: ReadonlyArray<keyof AdminLegalProfile> = [
  'id',
  'legalName',
  'registrationNumber',
  'taxId',
  'addressLine1',
  'city',
  'postalCode',
  'country',
  'locale',
  'jurisdiction',
  'signatoryName',
  'signatoryTitle',
  'signatoryEmail',
  'createdAtUtc',
  'updatedAtUtc',
];

const isLegalProfile = (value: unknown): value is AdminLegalProfile =>
  isRecord(value)
  && LegalProfileStringFields.every((field) => typeof value[field] === 'string')
  && (typeof value.companyId === 'number' || value.companyId === null)
  && isNullableString(value.addressLine2)
  && isNullableString(value.verifiedAtUtc)
  && typeof value.version === 'number';

export const getLegalProfileConflictCurrent = (error: unknown): AdminLegalProfile | null => {
  const current = responseData(error)?.current;
  return isLegalProfile(current) ? current : null;
};
